import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { AgGridReact } from 'ag-grid-react'
import { NameCellRenderer } from './nameColumn/NameCellRenderer'
import { TableContainer } from 'components/ideal-core-ui'
import { APP_CONSTANTS } from 'utils/constants'
import * as ACT from './actions'
import '!style-loader!css-loader!ag-grid-community/dist/styles/ag-grid.css' // eslint-disable-line
import '!style-loader!css-loader!ag-grid-community/dist/styles/ag-theme-balham.css' // eslint-disable-line
import '!style-loader!css-loader!styles/overrides/ag-grid.css' // eslint-disable-line
import { FaPlusCircle } from 'react-icons/fa'
import { toCompaniesRowData } from './serializers'
import { currencyFormatter, timezoneDate } from 'utils/helpers'
import { CompanyModalContainer } from 'components/common/modals/CompanyModalContainer'
import s from './CompaniesContainer.scss'
import { PageTitle } from 'components/common/PageTitle'
import { WebsiteCellRenderer } from './websiteColumn/WebsiteCellRenderer'
import { ParentCellRenderer } from './parentColumn/ParentCellRenderer'
import { AddButton } from 'components/common/AddButton'

const defaultSortModel = [
  {
    colId: 'name',
    sort: 'asc'
  }
]

const selectCompanies = createSelector([state => state.companies], companies => {
  return toCompaniesRowData(companies.rowData)
})

const CompaniesContainer = () => {
  const [showCompanyModal, setShowCompanyModal] = useState(false)

  const companies = useSelector(state => selectCompanies(state))
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch({
      type: ACT.COMPANIES_FETCH_REQUESTED
    })
  }, [dispatch])

  const onCompanyChange = params => {
    dispatch({
      type: ACT.COMPANY_UPDATE_REQUESTED,
      payload: {
        company: params.data,
        id: params.data.id
      }
    })
  }

  const columnDefs = [
    {
      headerName: 'Name',
      field: 'name',
      cellClass: 'deals-name-column',
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        path: 'companies',
        innerRendererFramework: NameCellRenderer
      }
    },
    {
      headerName: 'Address',
      field: 'address'
    },
    {
      headerName: 'City',
      field: 'city'
    },
    {
      headerName: 'State/Province/Region',
      field: 'state'
    },
    {
      headerName: 'Zip',
      field: 'zipcode'
    },
    {
      headerName: 'Phone',
      field: 'phone'
    },
    {
      headerName: 'Additional Phone',
      field: 'additionalPhone'
    },
    {
      headerName: 'Fax',
      field: 'fax'
    },
    {
      headerName: 'Website',
      field: 'website',
      cellRendererFramework: WebsiteCellRenderer
    },
    {
      headerName: 'Number of contacts',
      field: 'numberOfContacts'
    },
    {
      headerName: 'Current Open Opportunities Number',
      field: 'openDeals'
    },
    {
      headerName: 'Current Open Opportunities Value',
      field: 'openDealsValue',
      cellClass: 'center-align justify-content',
      valueFormatter: currencyFormatter
    },
    // {
    //   headerName: 'Won Opportunities Number',
    //   field: 'wonDeals'
    // },
    // {
    //   headerName: 'Won Opportunities Value',
    //   field: 'wonDealsValue',
    //   cellClass: 'center-align justify-content',
    //   valueFormatter: currencyFormatter
    // },
    {
      headerName: 'Company Type',
      field: 'types',
      cellRenderer: 'agGroupCellRenderer',
      valueGetter: params => {
        if (!params.data) return ''
        const { types } = params.data
        return types.map(type => type.name).join(', ') ?? ''
      }
    },
    {
      headerName: 'Market Segment',
      field: 'segment',
      valueGetter: params => {
        if (!params.data) return ''
        const { segment } = params.data
        return segment ? segment.name : ''
      }
    },
    {
      headerName: 'Company Size',
      field: 'size',
      cellRenderer: 'agGroupCellRenderer',
      valueGetter: params => {
        if (!params.data) return ''
        const { size } = params.data
        return size ? APP_CONSTANTS.CompanySize[size] : ''
      }
    },
    {
      headerName: 'Parent Company',
      field: 'parent',
      cellRendererFramework: ParentCellRenderer,
      valueGetter: params => {
        if (!params.data) return ''
        const { parent } = params.data
        return parent ? parent.name : ''
      }
    },
    {
      headerName: 'Labels',
      field: 'label',
      editable: true,
      onCellValueChanged: onCompanyChange
    },
    {
      headerName: 'Pre-qualified',
      valueGetter: params => {
        if (!params || !params.data) return ''
        return params.data.preQualified ? 'Yes' : 'No'
      },
      valueFormatter: params => {
        if (!params || !params.data) return ''
        return params.data.preQualified ? 'Yes' : 'No'
      }
    },
    {
      headerName: 'Pre-qualification Exp Date',
      valueGetter: params => {
        if (!params || !params.data) return ''
        return params.data.preQualifiedExpDate ? timezoneDate(params.data.preQualifiedExpDate) : ''
      },
      valueFormatter: params => {
        if (!params || !params.data) return ''
        return params.data.preQualifiedExpDate ? timezoneDate(params.data.preQualifiedExpDate) : ''
      }
    }
  ]

  const toggleCompanyModal = () => setShowCompanyModal(!showCompanyModal)

  return (
    <>
      <PageTitle view="Companies" />
      <section className={s.section}>
        <AddButton className={s.addBtn} onClick={toggleCompanyModal} cy="add_new_company_btn">
          <FaPlusCircle />
          Add Company
        </AddButton>
      </section>
      <TableContainer defaultSortModel={defaultSortModel} canSaveAsList tableKey="company">
        {gridData => <AgGridReact {...gridData} columnDefs={columnDefs} rowData={companies} />}
      </TableContainer>
      <CompanyModalContainer
        toggleCompanyModal={toggleCompanyModal}
        showCompanyModal={showCompanyModal}
        actionType={ACT.COMPANY_CREATE_REQUESTED}
      />
    </>
  )
}

export default CompaniesContainer
